import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  Ban, 
  CheckCircle2, 
  Clock, 
  XCircle,
  Loader2,
  FileText,
  Archive,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { getFileKeyDisplayName } from '@/lib/modules/file-keys';
import { FileKey } from '@/lib/modules/types';
import { cn } from '@/lib/utils';

export interface ClientUploadToken {
  id: string;
  client_name: string | null;
  client_email: string | null;
  requested_file_keys: string[] | null;
  expires_at: string;
  created_at: string;
  revoked_at: string | null;
  access_count: number;
  upload_count?: number;
}

interface ClientUploadLinksTableProps {
  tokens: ClientUploadToken[];
  onRefresh: () => void;
}

type TokenStatus = 'active' | 'revoked' | 'expired';

function getTokenStatus(token: ClientUploadToken): TokenStatus {
  if (token.revoked_at) return 'revoked';
  if (new Date(token.expires_at) < new Date()) return 'expired';
  return 'active';
}

function StatusBadge({ status }: { status: TokenStatus }) {
  switch (status) {
    case 'active':
      return (
        <Badge variant="default" className="gap-1 bg-green-600">
          <CheckCircle2 className="h-3 w-3" />
          Aktiv
        </Badge>
      );
    case 'revoked':
      return (
        <Badge variant="destructive" className="gap-1">
          <XCircle className="h-3 w-3" />
          Återkallad
        </Badge>
      );
    case 'expired':
      return (
        <Badge variant="secondary" className="gap-1">
          <Clock className="h-3 w-3" />
          Utgången
        </Badge>
      );
  }
}

function TokenRow({ 
  token, 
  onRevoke, 
  revoking,
  showActions = true 
}: { 
  token: ClientUploadToken; 
  onRevoke: (token: ClientUploadToken) => void;
  revoking: string | null;
  showActions?: boolean;
}) {
  const status = getTokenStatus(token);
  
  return (
    <TableRow>
      <TableCell>
        <div className="flex flex-col">
          <span className="font-medium">
            {token.client_name || 'Ej angiven'}
          </span>
          {token.client_email && (
            <span className="text-xs text-muted-foreground">
              {token.client_email}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1 max-w-[200px]">
          {token.requested_file_keys && token.requested_file_keys.length > 0 ? (
            token.requested_file_keys.slice(0, 3).map((key) => (
              <Badge key={key} variant="outline" className="text-xs">
                {getFileKeyDisplayName(key as FileKey)}
              </Badge>
            ))
          ) : (
            <span className="text-xs text-muted-foreground">Alla typer</span>
          )}
          {token.requested_file_keys && token.requested_file_keys.length > 3 && (
            <Badge variant="outline" className="text-xs">
              +{token.requested_file_keys.length - 3}
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="text-sm">
        {format(new Date(token.created_at), 'd MMM yyyy', { locale: sv })}
      </TableCell>
      <TableCell className="text-sm">
        {format(new Date(token.expires_at), 'd MMM yyyy', { locale: sv })}
      </TableCell>
      <TableCell className="text-center">
        <Badge variant={token.upload_count && token.upload_count > 0 ? "default" : "secondary"} className={token.upload_count && token.upload_count > 0 ? "bg-green-600" : ""}>
          {token.upload_count || 0} filer
        </Badge>
      </TableCell>
      <TableCell className="text-center">
        <Badge variant="secondary">{token.access_count}</Badge>
      </TableCell>
      <TableCell>
        <StatusBadge status={status} />
      </TableCell>
      <TableCell className="text-right">
        {showActions && status === 'active' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRevoke(token)}
            disabled={revoking === token.id}
            className="text-destructive hover:text-destructive"
          >
            {revoking === token.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Ban className="h-4 w-4 mr-1" />
                Återkalla
              </>
            )}
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

export function ClientUploadLinksTable({ tokens, onRefresh }: ClientUploadLinksTableProps) {
  const { t } = useTranslation();
  const [revoking, setRevoking] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<ClientUploadToken | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  // Separate active and inactive tokens
  const activeTokens = tokens.filter(t => getTokenStatus(t) === 'active');
  const inactiveTokens = tokens.filter(t => getTokenStatus(t) !== 'active');

  const handleRevoke = async (token: ClientUploadToken) => {
    setRevoking(token.id);
    try {
      const { error } = await supabase.functions.invoke('client-upload-revoke-token', {
        body: { tokenId: token.id }
      });

      if (error) throw error;

      toast.success('Länken har återkallats');
      onRefresh();
    } catch (err) {
      console.error('Revoke error:', err);
      toast.error('Kunde inte återkalla länken');
    } finally {
      setRevoking(null);
      setConfirmRevoke(null);
    }
  };

  if (tokens.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Inga kundlänkar skapade ännu</p>
      </div>
    );
  }

  const tableHeader = (
    <TableHeader>
      <TableRow>
        <TableHead>Kund</TableHead>
        <TableHead>Filtyper</TableHead>
        <TableHead>Skapad</TableHead>
        <TableHead>Giltig till</TableHead>
        <TableHead className="text-center">Uppladdade</TableHead>
        <TableHead className="text-center">Åtkomster</TableHead>
        <TableHead>Status</TableHead>
        <TableHead className="text-right">Åtgärder</TableHead>
      </TableRow>
    </TableHeader>
  );

  return (
    <>
      {/* Active tokens table */}
      {activeTokens.length > 0 ? (
        <Table>
          {tableHeader}
          <TableBody>
            {activeTokens.map((token) => (
              <TokenRow 
                key={token.id} 
                token={token} 
                onRevoke={setConfirmRevoke}
                revoking={revoking}
              />
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="text-center py-6 text-muted-foreground border rounded-lg">
          <CheckCircle2 className="h-6 w-6 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Inga aktiva kundlänkar</p>
        </div>
      )}

      {/* Inactive tokens collapsible */}
      {inactiveTokens.length > 0 && (
        <Collapsible open={showInactive} onOpenChange={setShowInactive} className="mt-4">
          <CollapsibleTrigger asChild>
            <Button 
              variant="ghost" 
              className="w-full justify-between py-2 px-3 h-auto hover:bg-muted/50"
            >
              <span className="flex items-center gap-2 text-muted-foreground">
                <Archive className="h-4 w-4" />
                <span className="text-sm">Ej aktiva ({inactiveTokens.length})</span>
              </span>
              <ChevronDown className={cn(
                "h-4 w-4 text-muted-foreground transition-transform duration-200",
                showInactive && "rotate-180"
              )} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <Table>
              {tableHeader}
              <TableBody>
                {inactiveTokens.map((token) => (
                  <TokenRow 
                    key={token.id} 
                    token={token} 
                    onRevoke={setConfirmRevoke}
                    revoking={revoking}
                    showActions={false}
                  />
                ))}
              </TableBody>
            </Table>
          </CollapsibleContent>
        </Collapsible>
      )}

      <AlertDialog open={!!confirmRevoke} onOpenChange={() => setConfirmRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Återkalla kundlänk?</AlertDialogTitle>
            <AlertDialogDescription>
              Länken för {confirmRevoke?.client_name || confirmRevoke?.client_email || 'denna kund'} 
              kommer att sluta fungera omedelbart. Kunden kommer inte längre kunna ladda upp filer 
              via denna länk. Denna åtgärd kan inte ångras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmRevoke && handleRevoke(confirmRevoke)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Återkalla länk
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
